/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { partTimePct } from "../lib/contract";
import { Loader2, User, MapPin, Briefcase } from "lucide-react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
};

/**
 * Read-only summary card shown at the top of the employee portal.
 * Three columns: anagrafica, residenza, contratto.
 * Edit flow is owned by the parent page (EmployeePortal) via the header's
 * "Modifica i tuoi dati" button — this card no longer renders its own edit CTA.
 */
export const EmployeeSummaryCard = ({ employeeId, refreshKey = 0 }) => {
    // `emp` triple-state: undefined = first load (pending), null = error/missing, object = ok.
    // Lets us derive `loading` without keeping a redundant state and avoid setState calls
    // in the synchronous portion of the effect (false-positive otherwise).
    const [emp, setEmp] = useState(undefined);

    useEffect(() => {
        if (!employeeId) return;
        let cancelled = false;
        // The actual setState calls happen *after* the network response, never
        // during the render phase — they're deferred via the microtask queue.
        void (async () => {
            try {
                const r = await api.get(`/employees/${employeeId}`);
                if (!cancelled) setEmp(r.data);
            } catch {
                if (!cancelled) setEmp(null);
            }
        })();
        return () => { cancelled = true; };
    }, [employeeId, refreshKey]);

    const loading = emp === undefined;

    if (loading) {
        return (
            <div className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="employee-summary-card">
                <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </div>
        );
    }

    if (!emp) return null;

    const fullAddress = [emp.indirizzo, emp.cap, emp.citta, emp.provincia ? `(${emp.provincia})` : null]
        .filter(Boolean).join(" ");

    return (
        <div
            className="mb-6 overflow-hidden rounded-lg border border-[#376DB3]/20 bg-white shadow-sm"
            data-testid="employee-summary-card"
        >
            <div className="flex items-center justify-between border-b border-[#376DB3]/15 bg-[#376DB3]/5 px-5 py-3">
                <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-[#376DB3]">
                    Riepilogo dati
                </h2>
            </div>
            <div className="grid gap-6 p-5 md:grid-cols-3">
                {/* Anagrafica */}
                <Section icon={User} title="Anagrafica">
                    <Row label="Cognome e Nome" value={`${emp.cognome || ""} ${emp.nome || ""}`.trim()} />
                    <Row label="Data di nascita" value={fmtDate(emp.data_nascita)} />
                    <Row label="Luogo di nascita" value={emp.luogo_nascita} />
                    <Row label="Codice Fiscale" value={emp.codice_fiscale} mono />
                    <Row label="Matricola" value={emp.matricola} mono />
                </Section>

                {/* Residenza */}
                <Section icon={MapPin} title="Residenza & contatti">
                    <Row label="Indirizzo" value={fullAddress || "—"} />
                    <Row label="Email" value={emp.email} />
                    <Row label="Cellulare" value={emp.cell_personale} />
                    <Row label="Telefono" value={emp.telefono_fisso} />
                </Section>

                {/* Contratto */}
                <Section icon={Briefcase} title="Rapporto di lavoro">
                    <Row label="Qualifica" value={emp.qualifica} />
                    <Row label="Tipo contratto" value={emp.contratto_tipo} />
                    <Row
                        label="Ore settimanali"
                        value={emp.contratto_ore
                            ? `${emp.contratto_ore}h${partTimePct(emp.contratto_tipo, emp.contratto_ore) ? ` (${partTimePct(emp.contratto_tipo, emp.contratto_ore)})` : ""}`
                            : null}
                    />
                    <Row label="Data assunzione" value={fmtDate(emp.data_assunzione)} />
                    {emp.contratto_scadenza && (
                        <Row label="Scadenza contratto" value={fmtDate(emp.contratto_scadenza)} />
                    )}
                    {emp.data_cessazione && (
                        <Row label="Data cessazione" value={fmtDate(emp.data_cessazione)} tone="warn" />
                    )}
                    <Row label="Pos. INPS" value={emp.pos_inps} mono />
                    <Row label="Pos. INAIL" value={emp.pos_inail} mono />
                </Section>
            </div>
            <div className="flex flex-col gap-1 border-t border-slate-100 bg-slate-50/60 px-5 py-2 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                <span>
                    Puoi modificare email, telefono e residenza dal pulsante <b>"Modifica i tuoi dati"</b> in alto. Per altre variazioni contatta l'ufficio HR.
                </span>
                {emp.updated_at && (
                    <span className="flex-shrink-0 italic text-slate-400" data-testid="emp-last-update">
                        Ultimo aggiornamento dati: <span className="font-mono-numbers not-italic">{fmtDate(emp.updated_at)}</span>
                    </span>
                )}
            </div>
        </div>
    );
};

const Section = ({ icon: Icon, title, children }) => (
    <div className="space-y-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-[#376DB3]">
            <Icon className="h-3.5 w-3.5" />
            {title}
        </div>
        <dl className="space-y-1.5">{children}</dl>
    </div>
);

const Row = ({ label, value, mono = false, tone = null }) => (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
        <dt className="text-[11px] uppercase tracking-wide text-slate-500">{label}</dt>
        <dd className={`${mono ? "font-mono-numbers" : ""} ${tone === "warn" ? "text-amber-700 font-medium" : "text-slate-800"}`}>
            {value || <span className="text-slate-400">—</span>}
        </dd>
    </div>
);
